import { type ReactNode, useId } from 'react';

type TextComposerProps = {
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  childFirstName?: string;
  disabled?: boolean;
  /** Photo attachment block (GIFTWEB-22) — Text tab only. */
  children?: ReactNode;
};

/**
 * Text tab of the message composer (OTC prototype, step 3): a capped textarea
 * with a live counter, and room underneath for the photo attachment block.
 */
function TextComposer({
  value,
  onChange,
  maxLength,
  childFirstName,
  disabled = false,
  children,
}: TextComposerProps) {
  const id = useId();
  const countId = `${id}-count`;
  const child = childFirstName?.trim() || 'them';

  return (
    <div>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        disabled={disabled}
        aria-label='Your message'
        aria-describedby={countId}
        data-testid='message-textarea'
        placeholder={`For example, share your favourite memory of ${child} from the past year, or a hope or message for their future.`}
        className='block min-h-[120px] w-full resize-none rounded-[13px] border-[1.5px] border-[#8b8a88] bg-white p-3.5 text-base leading-[1.5] text-[#1a201d] outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-[#6b736e] focus:border-[#256956] focus:ring-[3px] focus:ring-[#256956]/20 disabled:bg-[#f8f7f7] disabled:text-[#8b8a88] lg:min-h-[150px]'
      />
      <div className='mt-1.5 flex items-center justify-end'>
        <span
          id={countId}
          data-testid='message-char-count'
          className='shrink-0 text-[11.5px] text-[#a4a3a1]'
        >
          {value.length}/{maxLength}
          <span className='sr-only'> characters used</span>
        </span>
      </div>
      {children}
    </div>
  );
}

export default TextComposer;
